'use client'

import React, { useState, useRef, useCallback, useEffect } from 'react'
import Webcam from 'react-webcam'
import { doc, setDoc } from 'firebase/firestore'
import { ref, getDownloadURL } from 'firebase/storage'
import { firebaseDb, firebaseFunctions, firebaseStorage } from '@/firebase/init'
import { httpsCallable } from 'firebase/functions'
import { notify } from '@/utils/index'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import { CameraIcon } from '@heroicons/react/24/outline'
import {
  createUploadSession,
  handleImageConversion,
} from '@/components/page-partials/quiz/mobile-upload/index'

interface MobileInputProps {
  id: string
  onClose?: () => void
}

interface SignedUrlResponse {
  url: string
}

export default function MobileInput({ id, onClose }: MobileInputProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [webcamReady, setWebcamReady] = useState(false)
  const [fileLoading, setFileLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [permissionPrompt, setPermissionPrompt] = useState(false)
  const webcamRef = useRef<Webcam | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot()
      return imageSrc
    }
    return null
  }, [webcamRef])

  const handleUpload = async (file: File) => {
    if (!id) {
      return
    }

    if (webcamRef.current) {
      webcamRef.current.video?.pause()
    }

    let sessionDoc

    try {
      await createUploadSession(id)
      sessionDoc = doc(firebaseDb, 'uploads', id)
      await setDoc(
        sessionDoc,
        {
          status: 'uploading',
        },
        { merge: true },
      )

      const getSignedUrl = httpsCallable(firebaseFunctions, 'getSignedUrl')
      const response = await getSignedUrl({
        filename: `${id}.${file.name.split('.').pop()}`,
        contentType: file.type || 'image/heic', // Default to 'image/heic' if file.type is empty
      })

      const { url } = response.data as SignedUrlResponse

      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/heic', // Default to 'image/heic' if file.type is empty
        },
        body: file,
      })

      const storageRef = ref(firebaseStorage, `uploads/${id}.webp`)
      const downloadURL = await getDownloadURL(storageRef)

      await setDoc(sessionDoc, {
        status: 'completed',
        imageUrl: downloadURL,
        timestamp: Date.now(),
      })

      notify('success', 'Image uploaded successfully.')
      if (onClose) {
        onClose() // Close the modal on successful upload
      }
    } catch (error) {
      console.error('Image upload failed', error)
      notify('error', 'Image upload failed. Please try again.')

      await setDoc(
        sessionDoc,
        {
          status: 'failed',
        },
        { merge: true },
      )
    } finally {
      setFileLoading(false)
      setPreview(null)
      if (webcamRef.current) {
        webcamRef.current.video?.play()
      }
    }
  }

  const handleCaptureAndUpload = async () => {
    const imageSrc = capture()
    if (!imageSrc) {
      notify('error', 'Failed to capture image. Please try again.')
      return
    }
    setFileLoading(true)

    let file = await fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => new File([blob], `${id}.jpg`, { type: 'image/jpeg' }))

    file = await handleImageConversion(file)
    await handleUpload(file)
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let file = event.target.files?.[0]
    if (file) {
      const validImageTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/heic',
      ]

      if (
        !validImageTypes.includes(file.type) &&
        file.name.split('.').pop()?.toLowerCase() !== 'heic'
      ) {
        notify('error', 'Invalid file type. Please select an image.')

        return
      }

      if (file.size > 10 * 1024 * 1024) {
        notify('error', 'File size exceeds 10MB. Please select a smaller file.')

        return
      }

      setFileLoading(true)
      file = await handleImageConversion(file)

      const reader = new FileReader()
      reader.onloadend = () => setPreview(reader.result as string)
      reader.readAsDataURL(file)

      await handleUpload(file)
      // Reset file input value to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  useEffect(() => {
    const checkWebcamReady = () => {
      if (webcamRef.current && webcamRef.current.video?.readyState === 4) {
        setWebcamReady(true)
      }
    }

    const intervalId = setInterval(checkWebcamReady, 100)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null

    const updatePermissionStatus = () => {
      if (permissionStatus && permissionStatus.state === 'denied') {
        setPermissionPrompt(false)
        setErrorMessage(
          'Camera permission denied. Please use the file upload option.',
        )
      } else {
        setPermissionPrompt(true)
      }
    }

    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'camera' as PermissionName })
        .then((status) => {
          permissionStatus = status
          updatePermissionStatus()
          permissionStatus.onchange = updatePermissionStatus
        })
    } else {
      setPermissionPrompt(true)
    }

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [])

  const videoConstraints = {
    facingMode: 'environment',
  }

  const handleUserMediaError = (error: string | DOMException) => {
    console.error('UserMediaError:', error)
    setPermissionDenied(true)
    if (!fileLoading) {
      setErrorMessage(
        'Camera permission denied. Please allow camera access to use this feature.',
      )
    }
  }

  const requestCameraPermission = async () => {
    try {
      setPermissionDenied(false)
      setErrorMessage(null)
      await navigator.mediaDevices.getUserMedia({ video: true })
      setWebcamReady(true)
    } catch (error) {
      handleUserMediaError(error)
    }
  }

  const handleCameraRetry = () => {
    setPermissionDenied(false)
    setErrorMessage(null)
    requestCameraPermission()
  }

  return (
    <div className="relative flex flex-col items-center justify-between min-h-screen bg-v8-paper">
      <div className="relative w-full max-w-md min-h-screen">
        {!errorMessage && (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMediaError={handleUserMediaError}
            className={`w-full h-full object-cover ${
              fileLoading && preview ? 'invisible' : 'visible'
            }`}
          />
        )}
        {fileLoading && preview && (
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-contain"
          />
        )}
        {fileLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-v8-ink/30">
            <SvgSpinnerBlue classess="w-10 h-10" />
          </div>
        )}
        {!fileLoading && errorMessage && (
          <div className="absolute inset-0 flex items-center justify-center text-v8-blue text-center mb-4 px-4">
            {errorMessage}
          </div>
        )}
        {permissionDenied && permissionPrompt && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={handleCameraRetry}
              className="bg-v8-blue mt-[100px] text-white font-bold py-2 px-4 rounded-full"
              type="button"
            >
              Allow Camera Access
            </button>
          </div>
        )}
      </div>
      <div className="absolute bottom-28 flex space-x-4">
        <label
          htmlFor="file-upload"
          className={`bg-v8-blue text-white font-bold py-2 px-4 rounded-full w-[55px] h-[55px] flex items-center justify-center text-sm cursor-pointer ${
            fileLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          File
        </label>
        <input
          id="file-upload"
          type="file"
          accept="image/*,.heic"
          onChange={handleFileUpload}
          ref={fileInputRef}
          className="hidden"
          disabled={fileLoading}
        />
        {webcamReady && !errorMessage && (
          <button
            onClick={handleCaptureAndUpload}
            className={`bg-v8-blue text-white font-bold py-2 px-4 rounded-full w-[55px] h-[55px] flex items-center justify-center text-sm ${
              fileLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={fileLoading}
            type="button"
          >
            <CameraIcon className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  )
}
